import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_END_POINTS } from "../ApiEndPoints";
import axios from "axios";

// Helper function to get default preferences structure
const getDefaultPreferences = () => ({
  course_notifications: {
    receiveNotifications: false,
    getReminder: false,
    stayUpdated: false,
  },
  in_app_notifications: false,
  sms_notifications: false,
  email_notifications: false,
  notification_types: {}
});

const initialState = {
  isLoading: false,
  isError: false,
  message: null,
  preferences: null, // Will be set to default when user interacts or fetched from API
  showConfirmationModal: false,
};

// Get notification preferences
export const getNotificationPreferencesAPI = createAsyncThunk(
  "notificationPreferences/getNotificationPreferences",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token || localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      const response = await axios.get(API_END_POINTS.notificationPreferences, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response.data.message ||
            "Failed to fetch notification preferences",
        );
      } else {
        return rejectWithValue("Network error. Please try again.");
      }
    }
  },
);

// Update notification preferences
export const updateNotificationPreferencesAPI = createAsyncThunk(
  "notificationPreferences/updateNotificationPreferences",
  async (preferences, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token || localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      // Debug logging
      console.log("Sending preferences:", preferences);
      console.log(
        "course_notifications type:",
        typeof preferences.course_notifications,
      );
      console.log(
        "course_notifications value:",
        preferences.course_notifications,
      );

      const response = await axios.put(
        API_END_POINTS.notificationPreferences,
        preferences,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response.data.message ||
            "Failed to update notification preferences",
        );
      } else {
        return rejectWithValue("Network error. Please try again.");
      }
    }
  },
);

// Reset notification preferences
export const resetNotificationPreferencesAPI = createAsyncThunk(
  "notificationPreferences/resetNotificationPreferences",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token || localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("No authentication token found");
      }

      const response = await axios.post(
        API_END_POINTS.notificationPreferencesReset,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(
          error.response.data.message ||
            "Failed to reset notification preferences",
        );
      } else {
        return rejectWithValue("Network error. Please try again.");
      }
    }
  },
);

const notificationPreferencesSlice = createSlice({
  name: "notificationPreferences",
  initialState,
  reducers: {
    updateCourseNotifications: (state, action) => {
      // Initialize preferences if null (first time user interaction)
      if (!state.preferences) {
        state.preferences = getDefaultPreferences();
      }
      // Ensure course_notifications exists
      if (!state.preferences.course_notifications) {
        state.preferences.course_notifications = {
          receiveNotifications: false,
          getReminder: false,
          stayUpdated: false,
        };
      }
      state.preferences.course_notifications = {
        ...state.preferences.course_notifications,
        ...action.payload,
      };
    },
    updateInAppNotifications: (state, action) => {
      // Initialize preferences if null (first time user interaction)
      if (!state.preferences) {
        state.preferences = getDefaultPreferences();
      }
      state.preferences.in_app_notifications = action.payload;
    },
    updateSmsNotifications: (state, action) => {
      // Initialize preferences if null (first time user interaction)
      if (!state.preferences) {
        state.preferences = getDefaultPreferences();
      }
      state.preferences.sms_notifications = action.payload;
    },
    updateEmailNotifications: (state, action) => {
      // Initialize preferences if null (first time user interaction)
      if (!state.preferences) {
        state.preferences = getDefaultPreferences();
      }
      state.preferences.email_notifications = action.payload;
    },
    updateNotificationTypes: (state, action) => {
      // Initialize preferences if null (first time user interaction)
      if (!state.preferences) {
        state.preferences = getDefaultPreferences();
      }
      state.preferences.notification_types = {
        ...state.preferences.notification_types,
        ...action.payload,
      };
    },
    showConfirmationModal: (state) => {
      state.showConfirmationModal = true;
    },
    hideConfirmationModal: (state) => {
      state.showConfirmationModal = false;
    },
    clearMessage: (state) => {
      state.message = null;
      state.isError = false;
    },
  },
  extraReducers: (builder) => {
    // Get notification preferences
    builder.addCase(getNotificationPreferencesAPI.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
      state.message = null;
    });
    builder.addCase(getNotificationPreferencesAPI.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isError = false;
      if (action.payload.success) {
        // If data exists, use it; otherwise keep null (means no preferences saved yet)
        if (action.payload.data) {
          const data = action.payload.data.dataValues || action.payload.data;
          state.preferences = {
            course_notifications: data.course_notifications || {
              receiveNotifications: false,
              getReminder: false,
              stayUpdated: false,
            },
            in_app_notifications: data.in_app_notifications ?? false,
            sms_notifications: data.sms_notifications ?? false,
            email_notifications: data.email_notifications ?? false,
            notification_types: data.notification_types || {}
          };
        } else {
          // No preferences found - keep as null
          state.preferences = null;
        }
      }
    });
    builder.addCase(getNotificationPreferencesAPI.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message =
        action.payload || "Failed to fetch notification preferences";
    });

    // Update notification preferences
    builder.addCase(updateNotificationPreferencesAPI.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
      state.message = null;
    });
    builder.addCase(updateNotificationPreferencesAPI.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isError = false;
      if (action.payload.success && action.payload.data) {
        const data = action.payload.data.dataValues || action.payload.data;
        state.preferences = {
          course_notifications: data.course_notifications || {
            receiveNotifications: false,
            getReminder: false,
            stayUpdated: false,
          },
          in_app_notifications: data.in_app_notifications ?? false,
          sms_notifications: data.sms_notifications ?? false,
          email_notifications: data.email_notifications ?? false,
          notification_types: data.notification_types || {}
        };
        state.showConfirmationModal = true;
      }
    });
    builder.addCase(updateNotificationPreferencesAPI.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload || "Failed to update notification preferences";
    });

    // Reset notification preferences
    builder.addCase(resetNotificationPreferencesAPI.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
      state.message = null;
    });
    builder.addCase(
      resetNotificationPreferencesAPI.fulfilled,
      (state, action) => {
        state.isLoading = false;
        state.isError = false;
        if (action.payload?.success) {
          if (action.payload?.data) {
            state.preferences = {
              course_notifications:
                action.payload.data.course_notifications ||
                state.preferences.course_notifications,
              in_app_notifications:
                action.payload.data.in_app_notifications ??
                state.preferences.in_app_notifications,
              sms_notifications:
                action.payload.data.sms_notifications ??
                state.preferences.sms_notifications,
              email_notifications:
                action.payload.data.email_notifications ??
                state.preferences.email_notifications,
              notification_types:
                action.payload.data.notification_types ||
                state.preferences.notification_types,
            };
          }
          state.showConfirmationModal = true;
        }
      },
    );
    builder.addCase(
      resetNotificationPreferencesAPI.rejected,
      (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message =
          action.payload || "Failed to reset notification preferences";
      },
    );
  },
});

export const {
  updateCourseNotifications,
  updateInAppNotifications,
  updateSmsNotifications,
  updateEmailNotifications,
  updateNotificationTypes,
  showConfirmationModal,
  hideConfirmationModal,
  clearMessage,
} = notificationPreferencesSlice.actions;

export default notificationPreferencesSlice.reducer;
